import { colorsPalettes } from "@similarweb/styles";

import CountryService from "app/services/CountryService";
import { PdfExportService } from "app/services/PdfExportService";
import type { ILegendItems } from "app/components/React/Legends/legendsTypes";

import { getOthersDomainName } from "./const";

interface ExportOffset {
    x: number;
    y: number;
}
interface HeaderOptions {
    metricTitle: string;
    durations: string | string[];
    webSource: string;
    country: number;
}

const createHeaderHTML = ({ metricTitle, durations, webSource, country }: HeaderOptions): string => {
    const header = document.createElement("div");
    header.style.padding = "16px 16px 16px 24px";
    header.style.fontWeight = "500";
    header.style.color = colorsPalettes.carbon[400];
    header.style.display = "flex";
    header.style.alignItems = "baseline";

    const title = document.createElement("span");
    title.textContent = metricTitle;
    title.style.fontSize = "28px";
    title.style.fontWeight = "bold";
    title.style.marginRight = "16px";
    header.appendChild(title);

    const metadataContainer = document.createElement("div");
    metadataContainer.style.fontSize = "16px";
    metadataContainer.style.display = "flex";
    metadataContainer.style.alignItems = "center";

    const createDelimiter = () => {
        const delimiter = document.createElement("span");
        delimiter.textContent = " | ";
        delimiter.style.margin = "0 8px";
        delimiter.style.color = colorsPalettes.carbon[300];
        return delimiter;
    };

    const dates = document.createElement("span");
    dates.textContent = Array.isArray(durations) ? durations.join(", ") : durations;
    metadataContainer.appendChild(dates);
    metadataContainer.appendChild(createDelimiter());

    const countryText = document.createElement("span");
    countryText.textContent = CountryService.getCountryById(country).text;
    metadataContainer.appendChild(countryText);
    metadataContainer.appendChild(createDelimiter());

    const webSourceText = document.createElement("span");
    webSourceText.textContent = webSource;
    metadataContainer.appendChild(webSourceText);

    header.appendChild(metadataContainer);

    return header.outerHTML;
};

const createLegendsHTML = (visibleItems: ILegendItems) => {
    const container = document.createElement("div");
    container.style.cssText = "width: 300px; padding: 16px; background: white;";

    const header = document.createElement("div");
    header.style.cssText = `
        display: flex; 
        justify-content: space-between; 
        font-weight: 500; 
        font-size: 12px; 
        color: ${colorsPalettes.carbon[400]}; 
        margin-bottom: 12px;
    `;

    const domainTitle = document.createElement("span");
    domainTitle.textContent = "Domain";

    const trafficShareTitle = document.createElement("span");
    trafficShareTitle.textContent = "Traffic Share";

    header.appendChild(domainTitle);
    header.appendChild(trafficShareTitle);
    container.appendChild(header);

    visibleItems.forEach((item) => {
        const row = document.createElement("div");
        row.style.cssText = "display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;";

        // Left side - icon + name
        const leftSide = document.createElement("div");
        leftSide.style.cssText = "display: flex; align-items: center; flex: 1;";

        // Color dot
        const colorDot = document.createElement("div");
        colorDot.style.cssText = `
            width: 12px; 
            height: 12px; 
            border-radius: 50%; 
            background-color: ${item.color}; 
            margin-right: 8px; 
            flex-shrink: 0;
        `;
        leftSide.appendChild(colorDot);

        if (item.name !== getOthersDomainName()) {
            const logo = document.createElement("img");
            logo.src = item.favicon || "";
            logo.alt = `${String(item.name)} logo`;
            logo.style.cssText = "width: 16px; height: 16px; margin-right: 8px; flex-shrink: 0;";
            leftSide.appendChild(logo);
        }

        const nameSpan = document.createElement("span");
        nameSpan.textContent = String(item.name);
        nameSpan.style.cssText = "font-size: 14px; font-weight: 400; overflow: hidden; text-overflow: ellipsis;";
        leftSide.appendChild(nameSpan);

        const rightSide = document.createElement("span");
        rightSide.textContent = item.name !== getOthersDomainName() ? String(item.data || "") : "";
        rightSide.style.cssText = "font-size: 14px; font-weight: 400; margin-left: 16px;";

        row.appendChild(leftSide);
        row.appendChild(rightSide);
        container.appendChild(row);
    });

    return container;
};

export const exportChartAsPng = (
    chartRef: React.RefObject<HTMLDivElement>,
    dashboardKeys: ILegendItems,
    offset: ExportOffset = { x: 50, y: 30 },
    fileName: string = "MarketSharesChart",
    headerOptions: HeaderOptions = {
        metricTitle: "",
        durations: "",
        webSource: "",
        country: 999,
    },
) => {
    if (!chartRef.current) {
        return;
    }

    const styleHTML = PdfExportService.getStylesheetsForPng();
    const chartElement = chartRef.current;
    const clonedElement = chartElement.cloneNode(true) as HTMLDivElement;

    // No idea why but without custom CSS normalization the layout is broken in exported PNG
    // while it works fine locally without it, it breaks in sandbox and production
    const normalizeCSS = `
        <style>
            /* CSS Reset for export */
            * { box-sizing: border-box; }
            
            /* Override any flexbox issues for legends */
            [data-automation="market-shares-chart-legends"] {
                width: 300px !important;
                min-width: 300px !important;
                max-width: 300px !important;
                flex-shrink: 0 !important;
                padding: 16px !important;
            }
            
            /* Ensure outer wrapper has proper layout */
            [class*="MarketSharesChartOuterWrapper"] {
                display: flex !important;
                gap: 16px !important;
                align-items: flex-start !important;
                width: fit-content !important;
                max-width: none !important;
                padding: 16px !important;
            }
            
            /* Chart wrapper should take up remaining space */
            [class*="ChartWrapper"],
            [data-automation="market-shares-chart"] {
                flex: 1;
                min-width: 0;
                display: flex;
            }
            
            /* Ensure legend text doesn't overflow */
            [data-automation="market-shares-chart-legends"] span {
                white-space: nowrap !important;
                overflow: visible !important;
                text-overflow: unset !important;
            }
        </style>
    `;

    // Replace legends with custom HTML for clean appearance (no checkboxes, only selected items)
    const legendsContainer = clonedElement.querySelector('[data-automation="market-shares-chart-legends"]');
    if (legendsContainer) {
        const visibleItems = dashboardKeys.filter((key) => key.visible);
        legendsContainer.innerHTML = "";
        const newLegend = createLegendsHTML(visibleItems);
        legendsContainer.appendChild(newLegend);
    }

    const exportContainer = document.createElement("div");
    exportContainer.style.cssText = `
        background: white;
        padding: 0;
        margin: 0;
        width: fit-content;
        overflow: visible;
    `;

    const headerHTML = createHeaderHTML(headerOptions);
    exportContainer.innerHTML = headerHTML + clonedElement.outerHTML;

    const width = Math.max(chartElement.offsetWidth || 1366, 1200);
    const height = chartElement.offsetHeight || 400;

    return PdfExportService.downloadHtmlPngFedService(
        normalizeCSS + styleHTML + exportContainer.outerHTML,
        fileName,
        width + offset.x,
        height + 80 + offset.y,
    );
};
